import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Inject,
  PLATFORM_ID,
  ChangeDetectionStrategy
} from "@angular/core";
import { DeviceService } from "@core/services/device.service";
import { ImageViewerNavigationContext, ImageViewerService } from "@core/services/image-viewer.service";
import { MobileFooterService } from "@core/services/mobile-footer.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { fadeInOut } from "@shared/animations";
import { Subscription, fromEvent, auditTime, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-floating-presentation-button",
  template: `
    <div
      *ngIf="navigationContext && navigationContext.length > 1"
      [style.bottom.px]="bottomOffset"
      class="floating-button-container"
    >
      <button
        *ngIf="shouldShowScrollToTop"
        @fadeInOut
        [ngbTooltip]="'Scroll to top' | translate"
        (click)="scrollToTop()"
        class="scroll-to-top-button btn btn-primary"
        container="body"
      >
        <fa-icon class="me-0" icon="chevron-up"></fa-icon>
      </button>

      <button
        [ngbTooltip]="'Start presentation mode' | translate"
        (click)="startPresentationMode()"
        class="floating-presentation-button btn btn-primary"
        container="body"
      >
        <fa-icon class="me-0" icon="play"></fa-icon>
      </button>
    </div>
  `,
  styleUrls: ["./floating-presentation-button.component.scss"],
  animations: [fadeInOut],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FloatingPresentationButtonComponent implements OnInit, OnDestroy {
  @Input() navigationContext: ImageViewerNavigationContext;
  @Input() currentImageId: string | number;
  @Input() componentId: string; // Parent component's ID for nearEndOfContext routing
  @Input() element: HTMLElement; // Optional custom scroll element

  shouldShowScrollToTop = false;
  bottomOffset = 32; // Default 2rem
  private _offset = 0;
  private readonly _isBrowser: boolean;
  private _scrollSubscription: Subscription;
  private readonly _destroyed$ = new Subject<void>();

  constructor(
    private readonly imageViewerService: ImageViewerService,
    private readonly windowRefService: WindowRefService,
    private readonly mobileFooterService: MobileFooterService,
    private readonly deviceService: DeviceService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this._isBrowser = isPlatformBrowser(this.platformId);
  }

  startPresentationMode(): void {
    if (!this.navigationContext || this.navigationContext.length <= 1 || !this.componentId) {
      return;
    }

    // Find the index of the current image in the navigation context
    const currentIndex = this.currentImageId
      ? this.navigationContext.findIndex(
          item => item.imageId === this.currentImageId || item.imageId === this.currentImageId.toString()
        )
      : 0;

    console.log("[FloatingPresentationButton] Starting presentation mode with:", {
      componentId: this.componentId,
      navigationContext: this.navigationContext,
      currentIndex: currentIndex >= 0 ? currentIndex : 0
    });

    // Use the parent component's ID so nearEndOfContext events are properly routed
    this.imageViewerService.openPresentationMode(
      this.navigationContext,
      currentIndex >= 0 ? currentIndex : 0,
      this.componentId
    );
  }

  ngOnInit(): void {
    if (this._isBrowser) {
      this._initializeScrollListener();
      this._subscribeToFooterVisibility();
    }
  }

  ngOnDestroy(): void {
    if (this._scrollSubscription) {
      this._scrollSubscription.unsubscribe();
    }
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private _subscribeToFooterVisibility(): void {
    this.mobileFooterService
      .getFloatingElementOffset$()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(offset => {
        // Base offset: 16px (1rem) on mobile, 32px (2rem) on desktop
        const baseOffset = this.deviceService.smMax() ? 16 : 32;
        this.bottomOffset = baseOffset + offset;
        this.changeDetectorRef.markForCheck();
      });
  }

  scrollToTop(): void {
    if (!this._isBrowser) {
      return;
    }

    const element = this._getElement();
    if (!element) {
      return;
    }

    element.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }

  private _initializeScrollListener(): void {
    if (this._scrollSubscription) {
      this._scrollSubscription.unsubscribe();
    }

    const element = this._getElement();
    if (!element) {
      return;
    }

    this._scrollSubscription = fromEvent(element, "scroll")
      .pipe(auditTime(250))
      .subscribe(() => {
        this._offset = this._getScrollTop(element);
        this._updateShowState();
      });
  }

  private _updateShowState(): void {
    if (!this._isBrowser) {
      this.shouldShowScrollToTop = false;
      return;
    }

    const element = this._getElement();
    if (!element) {
      this.shouldShowScrollToTop = false;
      return;
    }

    const newShouldShow = this._offset > this._getClientHeight(element) / 2;

    if (this.shouldShowScrollToTop !== newShouldShow) {
      this.shouldShowScrollToTop = newShouldShow;
      this.changeDetectorRef.markForCheck();
    }
  }

  private _getElement(): HTMLElement | Window {
    if (!this._isBrowser) {
      return null;
    }

    return this.element || this.windowRefService.nativeWindow;
  }

  private _getScrollTop(element: HTMLElement | Window): number {
    if (element instanceof Window) {
      return (
        this.windowRefService.nativeWindow.scrollY || document.documentElement.scrollTop || document.body.scrollTop
      );
    } else {
      return element.scrollTop;
    }
  }

  private _getClientHeight(element: HTMLElement | Window): number {
    if (element instanceof Window) {
      return document.documentElement.clientHeight || document.body.clientHeight;
    } else {
      return element.clientHeight;
    }
  }
}
